import React, {useCallback, useEffect, useRef, useState} from 'react';

// DEVIATION from the plan: relative import instead of the '@' alias, for the
// same reason as the Task 1/2 files — jest.config.js has no moduleNameMapper
// for '@' and may not be touched, and this component is transitively imported
// by Task 6's test (index.test.tsx -> index.tsx -> CourseChapterScreen ->
// this file). See useGeneratedCourse.ts for the full explanation.
import {VideoSegment} from '../../../Hooks/LmsCourseApi/types';

type Props = {
  src: string;
  segment: VideoSegment | null;
  /** The window ended — played to its end, or the video could not play. */
  onSegmentEnd: (reason?: 'ended' | 'error') => void;
  posterTitle?: string;
  /** Start part-way into the window — "See the set-up again" replays the
   * last half-minute before the question, not the whole set-up. Clamped to
   * the window. */
  startAt?: number | null;
  /** Play without waiting for a press. Only ever set in response to one (the
   * student chose "Watch the coach's answer"), which is what lets the browser
   * allow it; a refusal just leaves the video paused. */
  autoPlay?: boolean;
};

/**
 * Slack around a window boundary, in seconds. `timeupdate` fires a few times a
 * second, so snapping back on any overshoot at all would fight normal playback
 * at the very end of a window.
 */
const SEEK_TOLERANCE = 0.75;

/**
 * Plays one window of a recording and stops.
 *
 * A generated course points every video chapter at the same file, so the player
 * must seek to the window's start and treat reaching its end as the end of the
 * chapter. Without the bound, chapter one would play the entire recording.
 */
export default function CourseVideoPlayer({
  src,
  segment,
  onSegmentEnd,
  posterTitle,
  startAt = null,
  autoPlay = false,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const endedRef = useRef(false);
  const seekedRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const [failed, setFailed] = useState(false);

  const start = segment?.start ?? 0;
  const end = segment?.end ?? null;
  // Where playback begins: the window's start, or part-way in when asked —
  // never before the window, and never so close to its end that the chapter
  // completes the moment it opens.
  const from =
    startAt != null && Number.isFinite(startAt)
      ? Math.min(Math.max(startAt, start), end != null ? Math.max(start, end - 1) : startAt)
      : start;

  // A new chapter can reuse the same <video> element and even the same src
  // (every chapter plays a slice of the same recording), so the "already
  // ended" guard and the seek must be re-armed explicitly on every segment
  // change rather than relying on a remount. Keying this effect on `start`
  // and `end` (not just `src`) is what makes that happen: a chapter change
  // always changes at least one of them.
  useEffect(() => {
    endedRef.current = false;
    seekedRef.current = false;
    setFailed(false);
    setElapsed(Math.max(0, from - start));
    const el = videoRef.current;
    if (el && Number.isFinite(from)) {
      try {
        el.currentTime = from;
      } catch {
        /* seeking before metadata is ready; onLoadedMetadata retries */
      }
    }
    if (el && autoPlay) {
      try {
        el.play()?.catch?.(() => {
          /* the browser refused; the student presses play */
        });
      } catch {
        /* no media support here; nothing to play */
      }
    }
  }, [src, start, end, from, autoPlay]);

  const finish = useCallback(() => {
    // Guards against a double fire: onTimeUpdate can keep firing after
    // currentTime has already crossed `end` (multiple events before pause
    // takes effect), and a native `ended` event can land around the same
    // moment if `end` lines up with the file's actual duration.
    if (endedRef.current) return;
    endedRef.current = true;
    videoRef.current?.pause();
    onSegmentEnd('ended');
  }, [onSegmentEnd]);

  const handleTimeUpdate = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;

    // Keep playback inside this chapter's window. The native controls expose
    // the whole 90-minute recording, so without this a student can scrub into
    // another chapter's content, and one drag to the right past `end` would
    // complete the chapter — every video gate in the course defeated in a
    // single gesture.
    if (el.currentTime < start - SEEK_TOLERANCE) {
      el.currentTime = start;
      return;
    }

    if (end != null && el.currentTime >= end) {
      // Order matters: reaching the end has to be judged *before* the window
      // is re-clamped. Clamping first snapped the playhead back to `end` and
      // returned, so playback ping-ponged on the boundary and the chapter
      // could never complete.
      if (!seekedRef.current) {
        finish();
        return;
      }
      // Dragged past the end rather than played to it — put it back, and do
      // not count it as watched.
      el.currentTime = end;
      return;
    }

    setElapsed(Math.max(0, el.currentTime - start));
  }, [start, end, finish]);

  /**
   * A chapter counts as watched when playback *reaches* the end of its window,
   * not when the playhead is dragged there. Cleared on play so a student who
   * scrubs and then watches the rest still completes it.
   */
  const handleSeeking = useCallback(() => {
    const el = videoRef.current;
    if (!el || end == null) return;
    if (el.currentTime >= end) seekedRef.current = true;
  }, [end]);

  const handleError = useCallback(() => {
    // A presigned URL expires in 48h and a course has 28 video chapters, so an
    // unplayable one is expected. Without this the student gets a silent black
    // box with Continue disabled forever and no way past it.
    setFailed(true);
    onSegmentEnd('error');
  }, [onSegmentEnd]);

  const total = end != null ? Math.max(0, end - start) : 0;
  const pct = total > 0 ? Math.min(100, (elapsed / total) * 100) : 0;

  return (
    // The LMS chapter player centres the video and caps it at 880px wide with a
    // 16px radius (nscl chapterStyles `videoLayout`). Without the cap the
    // recording fills the window and pushes the action row below the fold.
    <div
      style={{
        width: '100%',
        maxWidth: 880,
        alignSelf: 'center',
        background: '#000',
        borderRadius: 16,
        overflow: 'hidden',
      }}>
      <video
        ref={videoRef}
        src={src}
        controls
        style={{
          width: '100%',
          display: 'block',
          aspectRatio: '16 / 9',
          maxHeight: 494,
        }}
        onLoadedMetadata={() => {
          const el = videoRef.current;
          if (el && Number.isFinite(from)) el.currentTime = from;
        }}
        onTimeUpdate={handleTimeUpdate}
        onSeeking={handleSeeking}
        onPlay={() => {
          seekedRef.current = false;
          // With no window there is nothing to reach the end of, so the only
          // other trigger is the file's own `ended` event — which would mean
          // playing the whole 90-minute recording to clear one chapter.
          if (end == null) onSegmentEnd();
        }}
        onError={handleError}
        onEnded={finish}
      />
      {failed ? (
        <div
          style={{
            color: '#fff',
            fontSize: 14,
            padding: '16px 12px',
            lineHeight: 1.5,
          }}>
          This part of the recording could not be played. You can continue to
          the next chapter, or reload the page to try again.
        </div>
      ) : null}

      <div style={{height: 4, background: 'rgba(255,255,255,0.25)'}}>
        <div style={{height: '100%', width: `${pct}%`, background: '#3DAB9E'}} />
      </div>
      {posterTitle ? (
        <div style={{color: '#fff', fontSize: 13, padding: '8px 12px'}}>{posterTitle}</div>
      ) : null}
    </div>
  );
}
